import { Spinner } from '@creditornot/cb-progress';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, Redirect } from 'react-router';

import { getInboundMerchant, getOnboardingStatus } from 'apis/merchant-onboarding-service/api';

import SsmoPageWrapper from './SsmoPageWrapper';
import Wizard from './Wizard/Wizard';
import { SelfServeStepsIdentifiers } from './constants';
import ProductListForm from './forms/ProductListForm';
import BusinessInformationForm from './stepsContent/BusinessInformationForm';
import CommissionsStep from './stepsContent/CommissionsStep';
import MerchantDeviceSelectionForm from './stepsContent/DeviceSelectionStep';
import MenuPhotoshopOptionStep from './stepsContent/MenuPhotoshootOptionStep';
import ReviewInfo from './stepsContent/ReviewInfo';
import VenueOpeningHoursForm from './stepsContent/VenueOpeningHoursForm';
import { SelfServeStepsType, SelfServeWizardStep } from './types';
import useCountryConfig from '../../../common/hooks/useCountryConfig';
import { StyledAlert } from '../../shared-menu/pages/SharedMenuEditorPage/components/OfferDetailsForm/OfferDetailsFormFields.styles';
import { getCountryConfigs, getSsmoCountryConfigs } from '../SelfServeFormView/SelfServeSignUpForm/api';

const isSelfServeStep = (stepParam: string): boolean =>
  Object.values(SelfServeStepsIdentifiers).includes(stepParam as SelfServeStepsType);

const validateStepParam = (param: string | null) => {
  if (!param) {
    return SelfServeStepsIdentifiers.BusinessDetails; // TODO should get this value from the computer order
  }

  const isValidParam = isSelfServeStep(param);

  return isValidParam ? param.toLowerCase() : SelfServeStepsIdentifiers.BusinessDetails;
};

const SelfServeSignupWizard = () => {
  const intl = useIntl();
  const { search } = useLocation();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const [stepParam, setStepParam] = useState<string>(SelfServeStepsIdentifiers.BusinessDetails); // TODO should get this value from the computer order
  const queryClient = useQueryClient();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const merchantId = searchParams.get('merchantId') || '';

  const {
    data: inboundMerchant,
    isFetching: isFetchingInboundMerchant,
    error: inboundMerchantLoadError,
  } = useQuery(['/merchant-admin/inbound-merchants', merchantId], () => getInboundMerchant(merchantId), {
    refetchOnWindowFocus: false,
  });

  const {
    data: countriesConfig,
    isLoading: isCountriesConfigsLoading,
    error: countriesConfigLoadError,
  } = useQuery(['merchant-admin/config'], getCountryConfigs, {
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isSsmoCountryConfigsLoading,
    data: ssmoCountryConfig,
    error: ssmoCountryConfigLoadError,
  } = useQuery(['/merchant-admin/config/ssmo'], getSsmoCountryConfigs, {
    refetchOnWindowFocus: false,
  });

  const {
    data: onboardingStatusData,
    isLoading: isFetchingOnboardingStatus,
    error: onboardingStatusLoadError,
  } = useQuery(['merchant-admin/me/onboarding-status'], () => getOnboardingStatus());

  const {
    countryConfig,
    isFetching: isFetchingCountryConfig,
    error: countryConfigLoadError,
  } = useCountryConfig(inboundMerchant?.country || null, {
    refetchOnWindowFocus: false,
  });

  const wizardData: SelfServeWizardStep[] = useMemo(() => {
    if (!inboundMerchant || !ssmoCountryConfig || !countriesConfig || !countryConfig) {
      return [];
    }

    const TOTAL_NUMBER_OF_GROUPS = 4;

    const calculatePercentage = ({
      groupNumber,
      progressForGroup,
      totalNumberOfGroups,
    }: {
      groupNumber: number;
      progressForGroup: number;
      totalNumberOfGroups: number;
    }) => {
      // percentageFilled calculation for definition goes as follows
      // (current group number - 1 + progress for current group) / total number of groups
      // example:
      //  - The first step in the first group is supposed to fill half a bar. That's 1/2 of a bar. It's the group number 1 and the total number of groups is 4:
      //    percentageFilled: (1 - 1 + 1/2) / 4,
      //  - The second step in the third group is supposed to fill 2/3 of a bar. It's the group number 3 and the total number of groups is 4:
      //    percentageFilled: (3 - 1 + 2/3) / 4

      return (groupNumber - 1 + progressForGroup) / totalNumberOfGroups;
    };

    return [
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.business-info' }),
        identifier: SelfServeStepsIdentifiers.BusinessDetails,
        component: props => (
          <BusinessInformationForm {...props} inboundMerchant={inboundMerchant} countriesConfigs={countriesConfig} />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 1,
          progressForGroup: 1 / 2,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.business-info' }),
        identifier: SelfServeStepsIdentifiers.OpeningHours,
        component: props => <VenueOpeningHoursForm {...props} inboundMerchant={inboundMerchant} />,
        percentageFilled: calculatePercentage({
          groupNumber: 1,
          progressForGroup: 2 / 2,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.let-us-know' }),
        identifier: SelfServeStepsIdentifiers.ProductList,
        component: props => <ProductListForm {...props} inboundMerchant={inboundMerchant} />,
        percentageFilled: calculatePercentage({
          groupNumber: 2,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.getting-set-up' }),
        identifier: SelfServeStepsIdentifiers.MenuPhotoShoot,
        component: props => (
          <MenuPhotoshopOptionStep
            {...props}
            inboundMerchant={inboundMerchant}
            ssmoCountryConfig={ssmoCountryConfig}
            countryConfig={countryConfig}
          />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 3,
          progressForGroup: 1 / 3,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.getting-set-up' }),
        identifier: SelfServeStepsIdentifiers.DeviceSelection,
        component: props => (
          <MerchantDeviceSelectionForm
            {...props}
            inboundMerchant={inboundMerchant}
            ssmoCountryConfig={ssmoCountryConfig}
            countryConfig={countryConfig}
          />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 3,
          progressForGroup: 2 / 3,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.getting-set-up' }),
        identifier: SelfServeStepsIdentifiers.ServicePricing,
        component: props => (
          <CommissionsStep {...props} inboundMerchant={inboundMerchant} ssmoCountryConfig={ssmoCountryConfig} />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 3,
          progressForGroup: 3 / 3,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.last-review' }),
        identifier: SelfServeStepsIdentifiers.Review,
        component: props => <ReviewInfo {...props} inboundMerchant={inboundMerchant} countryConfig={countryConfig} />,
        percentageFilled: calculatePercentage({
          groupNumber: 4,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
    ];
  }, [inboundMerchant, ssmoCountryConfig, countriesConfig, formatMessage, countryConfig]);

  useEffect(() => {
    const stepParam = searchParams.get('step');
    const hasWizardData = wizardData.length > 0;

    if (!stepParam && hasWizardData) {
      history.push(`${search}&step=${wizardData[0].identifier}`);
      return;
    }
    if (hasWizardData) {
      const newStepParam = validateStepParam(searchParams.get('step')) || wizardData[0].identifier;
      setStepParam(newStepParam);
    }
  }, [history, search, searchParams, wizardData]);

  if (
    inboundMerchantLoadError ||
    countriesConfigLoadError ||
    ssmoCountryConfigLoadError ||
    onboardingStatusLoadError ||
    countryConfigLoadError
  ) {
    return <StyledAlert variant="error" title={intl.formatMessage({ id: 'self-serve-sign-up.error-unknown' })} />;
  }

  if (onboardingStatusData?.status === 'SSMO_ONBOARDING_PAGE') {
    return <Redirect to={'/onboarding-page'} />;
  }

  if (onboardingStatusData?.status === 'SSMO_CONTRACT_SIGNING_PAGE') {
    return <Redirect to={'/onboarding-contract'} />;
  }

  if (
    isFetchingInboundMerchant ||
    !inboundMerchant ||
    isCountriesConfigsLoading ||
    !countriesConfig ||
    isSsmoCountryConfigsLoading ||
    !ssmoCountryConfig ||
    isFetchingOnboardingStatus ||
    wizardData.length === 0 ||
    isFetchingCountryConfig
  ) {
    return (
      <SsmoPageWrapper>
        <Spinner size="large" />
      </SsmoPageWrapper>
    );
  }
  const invalidateCache = () => queryClient.invalidateQueries(['/merchant-admin/inbound-merchants', merchantId]);

  return (
    <SsmoPageWrapper>
      <Wizard
        stepsCount={4}
        invalidateCache={invalidateCache}
        wizardData={wizardData}
        currentStepIdentifier={stepParam}
      />
    </SsmoPageWrapper>
  );
};

export default SelfServeSignupWizard;
